import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { api, formatApiError } from '../lib/api';
import { Lock, Mail, Loader2 } from 'lucide-react';

const SSO_ERROR_MESSAGES = {
  not_provisioned: "That Microsoft account isn't set up in LegacyCRM yet. Ask your Super Admin to create your staff account first.",
  denied: 'Microsoft sign-in was cancelled.',
  invalid_state: 'Sign-in session expired — please try again.',
  invalid_token: "Couldn't verify your Microsoft identity — please try again.",
  token_exchange_failed: 'Microsoft sign-in failed — please try again.',
  no_id_token: 'Microsoft sign-in failed — please try again.',
  no_email: "Your Microsoft account doesn't expose an email address — contact your admin.",
};

const Login = () => {
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [ssoEnabled, setSsoEnabled] = useState(false);
  const [mode, setMode] = useState('login'); // login | forgot | forgot-sent
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotError, setForgotError] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ssoError = params.get('sso_error');
    if (ssoError) {
      setError(SSO_ERROR_MESSAGES[ssoError] || 'Microsoft sign-in failed — please try again.');
      window.history.replaceState({}, '', '/login');
    }
    api.get('/auth/microsoft/status').then(({ data }) => setSsoEnabled(!!data.enabled)).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try { await login(email, password); }
    catch (err) { setError(formatApiError(err)); }
    finally { setLoading(false); }
  };

  const submitForgot = async (e) => {
    e.preventDefault();
    setForgotError(''); setForgotLoading(true);
    try {
      await api.post('/auth/forgot-password', { email: forgotEmail });
      setMode('forgot-sent');
    } catch (err) {
      setForgotError(formatApiError(err));
    } finally {
      setForgotLoading(false);
    }
  };

  const signInWithMicrosoft = () => {
    window.location.href = `${process.env.REACT_APP_BACKEND_URL}/api/auth/microsoft/login`;
  };

  if (mode === 'forgot' || mode === 'forgot-sent') {
    return (
      <div className="login-shell" data-testid="login-screen">
        <div className="login-bg-grid" />
        {mode === 'forgot-sent' ? (
          <div className="login-card" data-testid="forgot-password-sent">
            <div className="login-brand">LegacyCRM</div>
            <div className="login-tag" style={{ marginBottom: 4 }}>Check your email</div>
            <div style={{ fontSize: 12.5, color: 'var(--text-sub, #7c8798)', lineHeight: 1.5, margin: '6px 0 16px' }}>
              If an account exists for <b>{forgotEmail}</b>, a password reset link is on its way. It expires in 1 hour.
            </div>
            <button type="button" className="login-submit" onClick={() => setMode('login')} data-testid="forgot-password-back-btn">
              Back to sign in
            </button>
          </div>
        ) : (
          <form className="login-card" onSubmit={submitForgot} data-testid="forgot-password-form">
            <div className="login-brand">LegacyCRM</div>
            <div className="login-tag">Reset your password</div>

            <label className="login-label"><Mail size={12} className="login-label-ico" /><span>Email</span></label>
            <input
              type="email" value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)}
              autoComplete="username" required autoFocus
              className="login-input" data-testid="forgot-password-email"
              placeholder="you@company.com"
            />

            {forgotError && <div className="login-error" data-testid="forgot-password-error">{forgotError}</div>}

            <button type="submit" disabled={forgotLoading} className="login-submit" data-testid="forgot-password-submit">
              {forgotLoading ? <><Loader2 size={13} className="spin" />Sending…</> : 'Send reset link'}
            </button>
            <button type="button" className="login-link-btn" onClick={() => setMode('login')} data-testid="forgot-password-cancel-btn">
              Back to sign in
            </button>
          </form>
        )}
      </div>
    );
  }

  return (
    <div className="login-shell" data-testid="login-screen">
      <div className="login-bg-grid" />
      <form className="login-card" onSubmit={submit} data-testid="login-form">
        <div className="login-brand">LegacyCRM</div>
        <div className="login-tag">Multi-line CRM · Sign in to continue</div>

        {ssoEnabled && (
          <>
            <button
              type="button"
              className="login-sso-btn"
              onClick={signInWithMicrosoft}
              data-testid="login-microsoft-btn"
            >
              <svg width="16" height="16" viewBox="0 0 21 21" aria-hidden="true">
                <rect x="1" y="1" width="9" height="9" fill="#f25022" />
                <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
                <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
                <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
              </svg>
              Sign in with Microsoft
            </button>
            <div className="login-sso-divider" data-testid="login-sso-divider"><span>or use your password</span></div>
          </>
        )}

        <label className="login-label">
          <Mail size={12} className="login-label-ico" />
          <span>Email</span>
        </label>
        <input
          type="email" value={email} onChange={(e) => setEmail(e.target.value)}
          autoComplete="username" required autoFocus
          className="login-input" data-testid="login-email"
          placeholder="admin@legacycrm.com"
        />

        <label className="login-label">
          <Lock size={12} className="login-label-ico" />
          <span>Password</span>
        </label>
        <input
          type="password" value={password} onChange={(e) => setPassword(e.target.value)}
          autoComplete="current-password" required
          className="login-input" data-testid="login-password"
          placeholder="••••••••"
        />
        <button
          type="button"
          className="login-forgot-link"
          onClick={() => { setMode('forgot'); setForgotEmail(email); }}
          data-testid="login-forgot-password-btn"
        >
          Forgot password?
        </button>

        {error && <div className="login-error" data-testid="login-error">{error}</div>}

        <button type="submit" disabled={loading} className="login-submit" data-testid="login-submit">
          {loading ? <><Loader2 size={13} className="spin" />Signing in…</> : 'Sign in'}
        </button>

        <div className="login-foot">
          Use your assigned credentials. New staff accounts are created by your super admin.
        </div>

        <div className="login-demo-creds" data-testid="login-demo-creds">
          <div className="login-demo-title">Try it now</div>
          <div className="login-demo-row">
            <button
              type="button"
              className="login-demo-pill"
              onClick={() => { setEmail('admin@legacycrm.com'); setPassword('LegacyCRM2026!'); }}
              data-testid="login-fill-admin"
            >
              Admin
            </button>
            <code>admin@legacycrm.com · LegacyCRM2026!</code>
          </div>
          <div className="login-demo-row">
            <button
              type="button"
              className="login-demo-pill"
              onClick={() => { setEmail('demo@legacycrm.com'); setPassword('Demo2026!'); }}
              data-testid="login-fill-demo"
            >
              Demo
            </button>
            <code>demo@legacycrm.com · Demo2026!</code>
          </div>
        </div>
      </form>
    </div>
  );
};

export default Login;
